import { TranslateService } from '@ngx-translate/core';
import { JobMetadataC } from '../../../module/calendar/full-calendar/classes/metadata/job-metadata';
import { OpportunityMetadataC } from '../../../module/calendar/full-calendar/classes/metadata/opportunity-metadata';
import { TaskMetadataC } from '../../../module/calendar/full-calendar/classes/metadata/task-metadata';
import { get } from 'lodash-es';
import { data_get, data_nullify, filled, generate_id } from '../../../shared/utils/common';
import { LooseObject } from '../../../objects/loose-object';
import { ProjectTaskCalendarEventMetadata } from '../../../module/calendar/full-calendar/classes/metadata/project-task-metadata';
import { to_numeric } from '../../../shared/utils/numbers';
import { UUID } from 'angular2-uuid';

export class SchedulerItem {

  task_name: string;
  task_priority: string;
  task_description: string;
  due_date: string;
  status: string;
  job_number: string;
  job_address: string;
  customer: string;
  department: string;
  opportunity_number: string;
  address: string;
  expected_close_date: string;
  job_due_date: string;

  metadata: SchedulableItem;
  module: string;

  // scheduler id if null, it means that it is new id
  id?: string;

  // for every plotted event id
  scheduler_id: string

  /**
   * set parent scheduler_id to identify it is child to scheduler id
   */
  parent_scheduler_id: string

  is_deleted: boolean

  note: string;

  scheduler_task_id?: string;

  estimated_duration?: number;

  resource_child?: LooseObject[];

  resource_child_count?: number;

  schedule_type?: string;

  get hasCustomer(): boolean {
    return filled(get(this, 'customer'));
  }

  get hasDepartment(): boolean {
    return filled(get(this, 'department'));
  }

  get hasJobAddress(): boolean {
    return filled(get(this, 'job_address'));
  }

  get hasQuoteAddress(): boolean {
    return filled(get(this, 'address'));
  }

  get hasDueDate(): boolean {
    return filled(get(this, 'job_due_date'));
  }

  get hasJobTasks(): boolean {
    return filled(get(this.metadata, 'tasks'));
  }

  constructor(properties: _SchedulerItemProps, translate?: TranslateService, scheduler_tasks_id: string = null, objSchedulerData: LooseObject = {}) {

    this.id = scheduler_tasks_id;
    this.scheduler_id = generate_id();
    this.is_deleted = properties.is_deleted;
    this.metadata = properties;
    this.note = get(objSchedulerData, 'note', null)
    this.scheduler_task_id = get(objSchedulerData, 'scheduler_task_id', null)

    if (properties instanceof TaskMetadataC) {

      this.module = 'tasks';
      if (translate) {

        this.task_priority = translate.instant(get(properties, 'priority', ' ') || ' ');
        this.status = translate.instant(get(properties, 'status', ' ') || ' ');
      } else {

        this.task_priority = properties.priority;
        this.status = properties.status;
      }

      this.task_name = properties.activity_name;
      this.task_description = properties.description;

      if (properties.job) {
        this.job_number = properties.job.job_number;
        this.job_address = properties.job.full_address;
        this.job_due_date = properties.job.due_date || '--';
        if (properties.job.customer) {

          this.customer = properties.job.customer.name;
        }
      }

      if (properties.opportunity) {
        this.opportunity_number = properties.opportunity.opportunity_number;
        this.address = properties.opportunity.full_address;
        this.expected_close_date = properties.opportunity.forecast_close_date;
        if (properties.opportunity.customer) {

          this.customer = properties.opportunity.customer.name;
        }
      }
    }

    if (properties instanceof JobMetadataC) {

      this.module = 'jobs';
      this.job_number = properties.job_number;
      this.job_address = properties.full_address;
      this.job_due_date = properties.due_date;
      if (properties.customer) {

        this.customer = properties.customer.name;
      }
    }

    if (properties instanceof OpportunityMetadataC) {

      this.module = 'opportunities';
      this.opportunity_number = properties.opportunity_number;
      this.address = properties.full_address;
      this.expected_close_date = properties.forecast_close_date;
      if (properties.customer) {

        this.customer = properties.customer.name;
      }

    }

    if (properties instanceof ProjectTaskCalendarEventMetadata) {
      this.module = 'project_tasks';
      this.task_name = properties.name;
      this.task_description = properties.description;

      this.job_number = data_nullify(data_get(properties, 'job.job_number'));
      this.job_address = data_nullify(data_get(properties, 'job.full_address'));
      this.job_due_date = data_nullify(data_get(properties, 'job.due_date'));

      this.customer = data_nullify(data_get(properties, 'job.customer.name'));
      this.estimated_duration = to_numeric(properties.estimated_duration);
    }

    this.department = data_get(properties, 'department.name');

    properties[this.module] = properties;

    this.due_date = get(objSchedulerData, 'due_date', null);
    this.estimated_duration = get(objSchedulerData, 'estimated_duration', null);
    this.resource_child = get(objSchedulerData, 'resource_child', []);
    this.resource_child_count = this.resource_child.length;
    this.schedule_type = get(objSchedulerData, 'schedule_type', null);
    this.parent_scheduler_id = get(objSchedulerData, 'parent_scheduler_id', null);
  }

  /**
   * generate new scheduler id
   */
  generateSchedulerId(): void {
    this.scheduler_id = generate_id();
  }

  setSchedulerId(id: string): void {
    this.scheduler_id = id as string;
  }

  /**
   * set the parent scheduler id
   *
   * @param strSchedulerId
   */
  setChildSchedulerId(strSchedulerId: string): void {
    this.parent_scheduler_id = strSchedulerId as string;
  }

  /**
   * remove the parent scheduler id
   *
   * @param strSchedulerId
   */
  deleteChildSchedulerId(): void {
    this.parent_scheduler_id = null;
  }
}

type _SchedulerItemProps = TaskMetadataC | JobMetadataC | OpportunityMetadataC | ProjectTaskCalendarEventMetadata;

export type SchedulableItem = _SchedulerItemProps;
